import moment from "moment";
import { LoadingOutlined } from "@ant-design/icons";
import { FC, useEffect, useState } from "react";
import { Button, Col, DatePicker, Input, Row, Select } from "antd";
import Checkbox from "antd/lib/checkbox/Checkbox";

import {
  ICountry,
  ICurrency,
  IJobGrade,
  ILongTermPlan,
  IModal,
  IPlanType,
} from "@types";
import Modal from "@components/Modal";
import { ALL_GRADES_VALUE, ALL_VALUE, getDaysOfMonth, showSuccessPopup } from "@utils";
import {
  installment_types,
  grant_types,
  vesting_types,
  grant_based_types,
  holding_periods,
} from "../config";
import {
  ErrorServices,
  useCreateLongTermPlanMutation,
  useFetchAllCompanyJobGradesQuery,
  useFetchCompanyCountriesQuery,
  useFetchCurrenciesQuery,
  useFetchLongTermPlanTypesQuery,
  useUpdateLongTermPlanMutation,
} from "@services";
import { useTypedSelector } from "@hooks";
import CustomModal from "@/components/customModel";
import { useFormValidation } from "@hooks";


const { Option } = Select;

interface IAddLongTermPlan extends IModal {
  selectedLongTermPlan: ILongTermPlan | null;
  setSelectedLongTermPlan: React.Dispatch<
    React.SetStateAction<ILongTermPlan | null>
  >;
}

interface IFormValues {
  name: string;
  plan_type_id: number | null;
  currency_id: number | null;
  country_ids: number[];
  grades: string[];
  holding_period: boolean;
  is_installment: boolean;
  is_amount: boolean;
  is_adhoc: boolean;
  date_of_grant: number | null;
  month_of_grant: number | null;
  installment_type: string;
  year_of_grant?: number | null;
  grant_type: string;
  vesting_type: string;
  grant_based_type: string;
  holding_period_no: number | null;
}

const AddLongTermPlan: FC<IAddLongTermPlan> = ({
  isVisible,
  setIsVisible,
  selectedLongTermPlan,
  setSelectedLongTermPlan,
}) => {
  const [global, setGlobal] = useState(false);
  const [gradeCountries, setGradeCountries] = useState<ICountry[]>([]);

  const { company } = useTypedSelector(state => state.auth?.user!);
  const { id: company_id } = company!;
  const { data: planTypesData, isLoading: isFetchingTypes } =
    useFetchLongTermPlanTypesQuery(null);
  const { data: planTypes } = planTypesData || {};
  const { data: currenciesData, isLoading: isFetchingCurrencies } =
    useFetchCurrenciesQuery(null);
  const { data: currencies } = currenciesData || {};
  const { data: jobGradesData, isLoading: isFetchingGrades } =
    useFetchAllCompanyJobGradesQuery({ company_id });
  const { data: jobGrades } = jobGradesData || {};
  const { data: countriesData, isLoading: isFetchingCountries } =
    useFetchCompanyCountriesQuery({
      company_id,
    });
  const { data: countries } = countriesData || {};
  const [createLongTermPlan, { isLoading: isCreating }] =
    useCreateLongTermPlanMutation();
  const [updateLongTermPlan, { isLoading: isUpdating }] =
    useUpdateLongTermPlanMutation();
  const [longTermPlan, setLongTermPlan] = useState<IFormValues>({
    name: "",
    plan_type_id: null,
    currency_id: null,
    country_ids: [],
    grades: [],
    holding_period: false,
    is_installment: false,
    is_amount: false,
    is_adhoc: false,
    date_of_grant: null,
    month_of_grant: null,
    installment_type: "",
    year_of_grant: null,
    grant_type: "",
    vesting_type: "",
    grant_based_type: "",
    holding_period_no: null,
  });
  // const handleGradeChange = (selectedGrades: string[]) => {
  //   const selectedJobGrades = jobGrades?.filter(jg =>
  //     selectedGrades.includes(jg.grade)
  //   );
  //   const mergedCountries =
  //     selectedJobGrades?.flatMap(jg => jg.countries || []) || [];
  //   const uniqueCountries = mergedCountries.filter(
  //     (c, idx, self) => c && idx === self.findIndex(sc => sc.id === c.id)
  //   );
  //   setGradeCountries(uniqueCountries);
  //   setLongTermPlan(prev => ({
  //     ...prev,
  //     grades: selectedGrades,
  //     country_ids: [],
  //   }));
  // };
  const handleGradeChange = (selectedGrades: string[]) => {
    // Check if user selected "All Grades"
    if (selectedGrades.includes(ALL_GRADES_VALUE)) {
      const allGrades = [...new Set(jobGrades?.map(jg => jg.grade).filter(Boolean))];

      // Get all countries for all grades
      const allCountries = jobGrades
        ?.flatMap(jg => jg.countries || [])
        .filter((c, idx, self) => c && idx === self.findIndex(sc => sc.id === c.id));

      setGradeCountries(allCountries);

      setLongTermPlan(prev => ({
        ...prev,
        grades: allGrades,
        country_ids: [],
      }));
      return;
    }

    // Normal selection
    const selectedJobGrades = jobGrades?.filter(jg => selectedGrades.includes(jg.grade));

    // Merge countries for selected grades
    const mergedCountries = selectedJobGrades?.flatMap(jg => jg.countries || []) || [];

    // Remove duplicates
    const uniqueCountries = mergedCountries.filter(
      (c, idx, self) => c && idx === self.findIndex(sc => sc.id === c.id)
    );

    // Update state
    setGradeCountries(uniqueCountries);
    setLongTermPlan(prev => ({
      ...prev,
      grades: selectedGrades,
      country_ids: [], // Reset countries when grades change
    }));
  };
  const handleGradeChangeForEdit = (selectedGrades: string[]) => {
    const selectedJobGrades = jobGrades?.filter(jg =>
      selectedGrades?.includes(jg.grade)
    );
    const mergedCountries =
      selectedJobGrades?.flatMap(jg => jg.countries || []) || [];
    const uniqueCountries = mergedCountries.filter(
      (c, idx, self) => c && idx === self.findIndex(sc => sc.id === c.id)
    );
    setGradeCountries(uniqueCountries);

    setLongTermPlan(prev => ({
      ...prev,
      grades: selectedGrades,
    }));
  };
  const handleSelectChange = (name: string, value: any) => {
    setLongTermPlan(prev => ({
      ...prev,
      [name]: value,
    }));
  };
  useEffect(() => {
    if (selectedLongTermPlan) {
      const {
        countries,
        currency: currency_id,
        holding_period,
        type,
        is_installment,
        is_amount,
        is_adhoc,
      } = selectedLongTermPlan;
      const normalizedCountries =
        countries?.map((c: any) => ({
          id: c.id,
          name: c?.country, // 🔥 KEY FIX
        })) || [];
      const longTermPlan: any = {
        ...selectedLongTermPlan,
        country_ids: countries?.map(({ id }) => id),
        currency_id,
        holding_period: !!holding_period,
        plan_type_id: type?.id,
        year_of_grant: moment().year(),
        vesting_type: is_installment ? "INSTALLMENT" : "CLIFF",
        grant_based_type: is_amount ? "VALUE" : "UNITS",
        grant_type: is_adhoc ? "ADHOC" : "ANNUAL",
      };
      delete longTermPlan?.countries;
      delete longTermPlan?.currency;
      delete longTermPlan?.id;
      delete longTermPlan?.is_global;
      delete longTermPlan?.is_all_grade;
      delete longTermPlan?.type;
      setLongTermPlan(longTermPlan);

      handleGradeChangeForEdit(longTermPlan?.grades);
      if (normalizedCountries?.length > 0) {
        const countryIds = normalizedCountries?.map(c => c?.id);
        handleSelectChange("country_ids", countryIds);
      }
    }
    return () => {
      setSelectedLongTermPlan(null);
    };
  }, [selectedLongTermPlan, , jobGradesData?.data]);

  const handleSubmit = async () => {
    try {
      const body = {
        ...longTermPlan,
        month_of_grant: longTermPlan?.month_of_grant || null,
        date_of_grant: longTermPlan?.date_of_grant || null,
        installment_type:
          longTermPlan?.vesting_type == "CLIFF"
            ? null
            : longTermPlan?.installment_type,
        country_ids: longTermPlan?.country_ids,
      };
      delete body.year_of_grant;


      if (selectedLongTermPlan) {
        await editShortTermPlan(body);
      } else {
        await addLongTermPlan(body);
      }
      setIsVisible(false);

      showSuccessPopup({
        title: `Long Term Incentive Plan ${selectedLongTermPlan ? "Updated!" : "Created!"
          }`,
        desc: `You have successfully ${selectedLongTermPlan ? "updated the" : "created a new"
          } LTI plan.`,
        role: "client",
      });
    } catch (error) {
      ErrorServices(error);
    }
  };

  const addLongTermPlan = async (body: IFormValues) =>
    await createLongTermPlan({ company_id, body }).unwrap();

  const editShortTermPlan = async (body: IFormValues) =>
    await updateLongTermPlan({
      company_id,
      id: selectedLongTermPlan?.id,
      body,
    }).unwrap();


  const validation = useFormValidation(longTermPlan, v => ({
    name: !v.name,
    plan_type_id: !v.plan_type_id,
    currency_id: !v.currency_id,
    grades: !v.grades || v.grades.length === 0,
    countries: !v.country_ids || v.country_ids.length === 0,
    vesting_type: !v.vesting_type,
    // grant_type: !v.grant_type,
    grant_based_type: !v.grant_based_type,
    installment_type:
      v.vesting_type === "INSTALLMENT" && !v.installment_type,
    // holding_period_no:
    //   v.holding_period && !v.holding_period_no,
  }));


  return (
    <CustomModal
      width="w-8/12"
      height="650px"
      footer={
        <>
          <button
            className="bg-themeGrey text-themeBlack font-medium text-lg py-2 px-14 rounded-md"
            onClick={() => setIsVisible(false)}
          >
            Cancel
          </button>
          <button
            className={`bg-themeBlue text-white font-medium text-lg py-2 px-14 rounded-md ${(!validation.isValid || isCreating || isUpdating) ? "opacity-50" : ""
              }`}
            disabled={!validation.isValid || isCreating || isUpdating}
            onClick={handleSubmit}
          >
            {isCreating || isUpdating ? (
              <LoadingOutlined className="spinner" />
            ) : selectedLongTermPlan ? (
              "Update"
            ) : (
              "Create"
            )}
          </button>

        </>
      }
      title={`${selectedLongTermPlan ? "Update" : "Create"} LTI Plan`}
      isVisible={isVisible}
    >
      <>
        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Plan Name
            </label>
            <Input
              className={`w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
              value={longTermPlan?.name}
              onBlur={() => validation.touch("name")}
              onChange={event =>
                setLongTermPlan(prev => ({
                  ...prev,
                  name: event?.target.value,
                }))
              }
              size="large"
              placeholder="Enter plan name here..."
            />
          </Col>
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Vesting Type
            </label>
            <Select
              size="large"
              className="custom-select"
              showArrow={true}
              showSearch={false}
              value={longTermPlan?.vesting_type || undefined}
              onBlur={() => validation.touch("vesting_type")}
              onChange={vesting_type => {
                if (vesting_type == "INSTALLMENT") {
                  setLongTermPlan(prev => ({ ...prev, is_installment: true }));
                } else {
                  setLongTermPlan(prev => ({ ...prev, is_installment: false }));
                }
                setLongTermPlan(prev => ({ ...prev, vesting_type }));
              }}
              placeholder="Select installment type from here..."
            >
              {vesting_types.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Holding Period
            </label>
            <Select
              size="large"
              className="custom-select"
              showArrow={true}
              showSearch={false}
              value={longTermPlan?.holding_period_no || undefined}
              // onBlur={() => validation.touch("holding_period_no")}
              onChange={count => {
                setLongTermPlan(prev => ({
                  ...prev,
                  holding_period_no: count,
                  holding_period: true,
                }));
              }}
              placeholder="Select grant based type from here..."
            >
              {holding_periods.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Installment Schedule
            </label>
            <Select
              size="large"
              className="custom-select"
              showArrow={true}
              showSearch={false}
              disabled={longTermPlan?.vesting_type == "CLIFF"}
              value={longTermPlan?.installment_type || undefined}
              onBlur={() => validation.touch("installment_type")}
              onChange={installment_type =>
                setLongTermPlan(prev => ({ ...prev, installment_type }))
              }
              placeholder="Select installment type from here..."
            >
              {installment_types.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Type of Stock
            </label>
            <Select
              loading={isFetchingTypes}
              size="large"
              className="custom-select"
              showArrow={true}
              showSearch={false}
              value={longTermPlan?.plan_type_id || undefined}
              onBlur={() => validation.touch("plan_type_id")}
              onChange={plan_type_id =>
                setLongTermPlan(prev => ({ ...prev, plan_type_id }))
              }
              placeholder="Select stock type from here..."
            >
              {planTypes?.map((type: IPlanType) => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Currency
            </label>
            <Select
              className="custom-select"
              showArrow={true}
              showSearch={true}
              loading={isFetchingCurrencies}
              filterOption={(input: any, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              value={longTermPlan?.currency_id || undefined}
              onBlur={() => validation.touch("currency_id")}
              onChange={currency_id =>
                setLongTermPlan(prev => ({
                  ...prev,
                  currency_id,
                }))
              }
              size="large"
              placeholder="Select currency from here..."
            >
              {currencies?.map(({ id, name, code }: ICurrency) => (
                <Option key={id} value={id}>
                  {`${name} (${code})`}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row justify="space-between">
          {/* <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Month of Grant
            </label>
            <br />
            <DatePicker
              className={`w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
              size="large"
              picker="month"
              value={
                selectedLongTermPlan
                  ? moment(
                      `${longTermPlan?.year_of_grant}-${longTermPlan?.month_of_grant}`
                    )
                  : undefined
              }
              onChange={date => {
                setLongTermPlan(prev => ({
                  ...prev,
                  month_of_grant: moment(date).month() + 1,
                  year_of_grant: moment(date).year(),
                }));
              }}
              placeholder="Select month of grant from here..."
            />
          </Col>
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Date of Grant
            </label>

            <Select
              loading={isFetchingGrades}
              showArrow={true}
              className="custom-select"
              showSearch={true}
              size="large"
              value={longTermPlan?.date_of_grant || undefined}
              onChange={date_of_grant =>
                setLongTermPlan(prev => ({ ...prev, date_of_grant }))
              }
              placeholder="Select date of grant from here..."
            >
              {getDaysOfMonth({
                month: longTermPlan?.month_of_grant || undefined,
                year: longTermPlan?.year_of_grant || undefined,
              })?.map(day => (
                <Option key={day} value={day}>
                  {day}
                </Option>
              ))}
            </Select>
          </Col> */}

          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Grades
            </label>
            <Select
              className="custom-select"
              loading={isFetchingGrades}
              mode="multiple"
              showArrow
              size="large"
              showSearch={true}
              placeholder="Select grades from here..."
              value={longTermPlan?.grades}
              onBlur={() => validation.touch("grades")}
              onChange={handleGradeChange}
            >
              {/* All Grades option (only show if not all selected) */}
              {![
                ...new Set(jobGrades?.map(jgd => jgd?.grade).filter(Boolean)),
              ].every(g => longTermPlan?.grades?.includes(g)) && (
                  <Option key={ALL_GRADES_VALUE} value={ALL_GRADES_VALUE}>
                    All grades
                  </Option>
                )}

              {[...new Set(jobGrades?.map(jgd => jgd?.grade).filter(Boolean))].map(
                grade => (
                  <Option key={grade} value={grade}>
                    {grade}
                  </Option>
                )
              )}
            </Select>
          </Col>

          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Countries
            </label>
            {/* <Select
              className="custom-select"
              disabled={global}
              filterOption={(input: any, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              loading={isFetchingCountries}
              showArrow={true}
              showSearch={true}
              mode="multiple"
              size="large"
              onChange={country_ids =>
                setLongTermPlan(prev => ({ ...prev, country_ids }))
              }
              value={longTermPlan?.country_ids}
              placeholder="Select countries from here..."
            >
              {countries?.map(({ id, name }: ICountry) => (
                <Option key={id} value={id}>
                  {name}
                </Option>
              ))}
            </Select> */}

            <Select
              className="custom-select"
              filterOption={(input: any, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              loading={isFetchingCountries}
              showArrow={true}
              showSearch={true}
              disabled={longTermPlan?.grades?.length === 0}
              mode="multiple"
              size="large"
              // onChange={selectedCountryIds => {
              //   if (selectedCountryIds.includes("Global")) {
              //     setLongTermPlan(prev => ({
              //       ...prev,
              //       country_ids: countries.map(({ id }) => id),
              //     }));
              //     setGlobal(true);
              //   } else {
              //     setLongTermPlan(prev => ({
              //       ...prev,
              //       country_ids: selectedCountryIds,
              //     }));
              //     setGlobal(false);
              //   }
              // }}
              onBlur={() => validation.touch("countries")}
              onChange={(country_ids: any[]) => {
                if (country_ids.includes(ALL_VALUE)) {
                  setLongTermPlan(prev => ({
                    ...prev,
                    country_ids: gradeCountries?.map(c => c.id) || [],
                  }));
                } else {
                  setLongTermPlan(prev => ({
                    ...prev,
                    country_ids,
                  }));
                }
              }}
              value={longTermPlan?.country_ids} // If Global is selected, show "All", otherwise show selected countries
              placeholder="Select countries from here..."
            >
              {/* Add "All" option */}
              {/* <Option key="all" value="Global">
                Global
              </Option> */}
              {!gradeCountries?.every(c =>
                longTermPlan?.country_ids?.includes(c.id)
              ) && (
                  <Option key={ALL_VALUE} value={ALL_VALUE}>
                    All Countries
                  </Option>
                )}
              {/* Render country options */}
              {gradeCountries?.map(({ id, name }: ICountry) => (
                <Option key={id} value={id}>
                  {name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Grant Type
            </label>
            <Select
              size="large"
              className="custom-select"
              showArrow={true}
              showSearch={false}
              value={longTermPlan?.grant_type || undefined}
              // onBlur={() => validation.touch("grant_type")}
              onChange={grant_type => {
                if (grant_type == "ADHOC") {
                  setLongTermPlan(prev => ({ ...prev, is_adhoc: true }));
                } else {
                  setLongTermPlan(prev => ({ ...prev, is_adhoc: false }));
                }
                setLongTermPlan(prev => ({ ...prev, grant_type }));
              }}
              placeholder="Select grant type from here..."
            >
              {grant_types.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4">
              Date of Grant
            </label>
            <br />
            <DatePicker
              className={`w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
              size="large"
              disabled={longTermPlan?.grant_type === "ADHOC"}
              picker="date"
              value={
                longTermPlan?.month_of_grant && longTermPlan?.date_of_grant
                  ? moment(
                    `${new Date().getFullYear()}-${longTermPlan.month_of_grant
                    }-${longTermPlan.date_of_grant}`,
                    "YYYY-MM-DD"
                  )
                  : undefined
              }
              onChange={date => {
                if (date) {
                  setLongTermPlan(prev => ({
                    ...prev,
                    month_of_grant: moment(date).month() + 1, // Update month (1-based index)
                    date_of_grant: moment(date).date(), // Update day
                  }));
                }
              }}
              placeholder="Select date of grant from here..."
              format="MM/DD" // Show only the month and day format
            />
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Grant Based Type
            </label>
            <Select
              size="large"
              className="custom-select"
              showArrow={true}
              showSearch={false}
              value={longTermPlan?.grant_based_type || undefined}
              onBlur={() => validation.touch("grant_based_type")}
              onChange={grant_based_type => {
                if (grant_based_type === "VALUE") {
                  setLongTermPlan(prev => ({ ...prev, is_amount: true }));
                }
                if (grant_based_type === "UNITS") {
                  setLongTermPlan(prev => ({ ...prev, is_amount: false }));
                }
                setLongTermPlan(prev => ({ ...prev, grant_based_type }));
              }}
              placeholder="Select grant based type from here..."
            >
              {grant_based_types.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        {/* <Row justify="space-between" className="my-6">
          <Col className="align-center-vertically" span={24}>
            <Checkbox
              checked={global}
              onChange={e => setGlobal(e.target.checked)}
            >
              <span className={"font-medium text-lg text-themeInputTitleColor"}>
                Global
              </span>
            </Checkbox>
            <Checkbox
              checked={longTermPlan?.holding_period}
              onChange={e =>
                setLongTermPlan(prev => ({
                  ...prev,
                  holding_period: e.target.checked,
                }))
              }
            >
              <span className={"font-medium text-lg text-themeInputTitleColor"}>
                Period
              </span>
            </Checkbox>
            <Checkbox
              checked={longTermPlan?.is_installment}
              onChange={e =>
                setLongTermPlan(prev => ({
                  ...prev,
                  is_installment: e.target.checked,
                }))
              }
            >
              <span className={"font-medium text-lg text-themeInputTitleColor"}>
                Installment
              </span>
            </Checkbox>
            <Checkbox
              checked={longTermPlan?.is_amount}
              onChange={e =>
                setLongTermPlan(prev => ({
                  ...prev,
                  is_amount: e.target.checked,
                }))
              }
            >
              <span className={"font-medium text-lg text-themeInputTitleColor"}>
                Amount
              </span>
            </Checkbox>
            <Checkbox
              checked={longTermPlan?.is_adhoc}
              onChange={e =>
                setLongTermPlan(prev => ({
                  ...prev,
                  is_adhoc: e.target.checked,
                }))
              }
            >
              <span className={"font-medium text-lg text-themeInputTitleColor"}>
                Adhoc
              </span>
            </Checkbox>
          </Col>
        </Row> */}
      </>
    </CustomModal>
  );
};

export default AddLongTermPlan;
